import React, { useState, useCallback } from 'react';
import { Level, LevelType } from '../types/Level';
import { Edit, Trash2, Star, Image, FileText, Grid, MapPin, List, Music, CheckCircle, XCircle, TrendingUp, Info } from 'lucide-react';
import { LevelAnalytics } from '../types/Analytics';

interface LevelCardProps {
  level: Level;
  onEdit: (level: Level) => void;
  onDelete: (id: string) => void;
  analytics?: LevelAnalytics;
}

const LevelCard: React.FC<LevelCardProps> = ({ level, onEdit, onDelete, analytics }) => {
  const [imageEnlarged, setImageEnlarged] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageSize, setImageSize] = useState<number | undefined>(level.imageSize);

  const loadImage = useCallback(async () => {
    if (!level.image || imageUrl || imageLoading) return;

    setImageLoading(true);
    try {
      const { getStorage, ref, getDownloadURL, getMetadata } = await import('firebase/storage');
      const storage = getStorage();
      const imageRef = ref(storage, level.image);
      const url = await getDownloadURL(imageRef);
      setImageUrl(url);

      // Fetch image size if not already set
      if (!imageSize) {
        try {
          const metadata = await getMetadata(imageRef);
          setImageSize(metadata.size);
        } catch (error) {
          console.error('Failed to load image metadata:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load image:', error);
    } finally {
      setImageLoading(false);
    }
  }, [level.image, imageUrl, imageLoading, imageSize]);

  // Load image when component mounts if level has an image
  React.useEffect(() => {
    if (level.image) {
      loadImage();
    }
  }, [level.image, loadImage]);

  const getLevelTypeIcon = (type: LevelType) => {
    switch (type) {
      case LevelType.CategoryWords:
        return <List size={16} />;
      case LevelType.KnownSentence:
        return <FileText size={16} />;
      case LevelType.WordSearch:
        return <Grid size={16} />;
      case LevelType.WhatsInThePicture:
        return <Image size={16} />;
      case LevelType.WhereIsThisLocated:
        return <MapPin size={16} />;
      case LevelType.WhatsTheSong:
        return <Music size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  const getLevelTypeColor = (type: LevelType) => {
    switch (type) {
      case LevelType.CategoryWords:
        return '#10b981';
      case LevelType.KnownSentence:
        return '#3b82f6';
      case LevelType.WordSearch:
        return '#8b5cf6';
      case LevelType.WhatsInThePicture:
        return '#f59e0b';
      case LevelType.WhereIsThisLocated:
        return '#ef4444';
      case LevelType.WhatsTheSong:
        return '#ec4899';
      default:
        return '#6b7280';
    }
  };

  const renderDifficultyStars = (difficulty: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < difficulty ? 'difficulty-star' : 'difficulty-star empty'}
        fill={i < difficulty ? 'currentColor' : 'none'}
      />
    ));
  };

  const renderLevelDetails = () => {
    switch (level.levelType) {
      case LevelType.CategoryWords:
        return (
          <>
            <p><strong>Category:</strong> {level.category}</p>
            <p><strong>Words:</strong> {level.words?.length || 0} words</p>
            <p><strong>Key Letters:</strong> {level.keyLetters?.length || 0} letters</p>
          </>
        );
      case LevelType.KnownSentence:
        return (
          <>
            <p><strong>Sentence:</strong> {level.sentence}</p>
            <p><strong>Key Letters:</strong> {level.keyLetters?.length || 0} letters</p>
            {level.image && renderImageThumbnail()}
          </>
        );
      case LevelType.WordSearch:
        return (
          <>
            <p><strong>Category:</strong> {level.category}</p>
            {level.wordSearchGrid ? (
              <>
                <p><strong>Grid Size:</strong> {level.wordSearchGrid.width}×{level.wordSearchGrid.height}</p>
                <p><strong>Words Placed:</strong> {level.wordSearchGrid.wordPositions?.length || 0} words</p>
              </>
            ) : (
              <>
                <p><strong>Words to Find:</strong> {level.words?.length || 0} words</p>
                <p style={{ color: '#f59e0b', fontSize: '0.85rem' }}>⚠️ Legacy format - needs grid migration</p>
              </>
            )}
          </>
        );
      case LevelType.WhatsInThePicture:
      case LevelType.WhatsTheSong:
        return (
          <>
            <p><strong>Sentence:</strong> {level.sentence}</p>
            <p><strong>Key Letters:</strong> {level.keyLetters?.length || 0} letters</p>
            {level.image && renderImageThumbnail()}
          </>
        );
      case LevelType.WhereIsThisLocated:
        return (
          <>
            <p><strong>City:</strong> {level.sentence}</p>
            {level.locationName && <p><strong>Location:</strong> {level.locationName}</p>}
            <p><strong>Key Letters:</strong> {level.keyLetters?.length || 0} letters</p>
            {level.image && renderImageThumbnail()}
          </>
        );
      default:
        return null;
    }
  };

  const renderImageThumbnail = () => {
    if (!level.image) return null;

    // Format bytes to human-readable size
    const formatBytes = (bytes: number | undefined) => {
      if (!bytes || bytes === 0) return 'Loading...';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    return (
      <>
        <div
          style={{
            marginTop: '0.5rem',
            position: 'relative'
          }}
        >
          {imageSize !== undefined && (
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.25rem'
            }}>
              <strong>Image Size:</strong> {formatBytes(imageSize)}
            </div>
          )}

          {imageLoading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              <Image size={16} color="#6b7280" />
              <span style={{ color: '#6b7280' }}>Loading image...</span>
            </div>
          )}

          {imageUrl && (
            <div
              style={{
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onMouseDown={() => setImageEnlarged(true)}
              onMouseUp={() => setImageEnlarged(false)}
              onClick={(e) => {
                e.preventDefault();
                setImageEnlarged(!imageEnlarged);
              }}
            >
              <img
                src={imageUrl}
                alt="Level thumbnail"
                crossOrigin="anonymous"
                style={{
                  maxWidth: '100px',
                  maxHeight: '100px',
                  display: 'block',
                  borderRadius: '4px',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              />
            </div>
          )}
        </div>

        {/* Full-page modal overlay when enlarged */}
        {imageEnlarged && imageUrl && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              cursor: 'pointer'
            }}
            onMouseUp={() => setImageEnlarged(false)}
            onMouseLeave={() => setImageEnlarged(false)}
            onClick={() => setImageEnlarged(false)}
          >
            <img
              src={imageUrl}
              alt="Level preview enlarged"
              crossOrigin="anonymous"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
              }}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="level-card">
      <div className="level-header">
        <div
          className="level-type"
          style={{ backgroundColor: getLevelTypeColor(level.levelType) }}
        >
          {getLevelTypeIcon(level.levelType)}
          {level.levelType.replace(/([A-Z])/g, ' $1').trim()}
        </div>
        <div className="level-difficulty">
          {renderDifficultyStars(level.difficulty)}
        </div>
      </div>

      <div className="level-info">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3>Level {level.id}</h3>
          <div title={level.funFact ? `Fun Fact: ${level.funFact}` : 'No Fun Fact'} style={{ cursor: 'help' }}>
            <Info
              size={18}
              color={level.funFact ? '#8b5cf6' : '#d1d5db'}
              style={{ opacity: level.funFact ? 1 : 0.4 }}
            />
          </div>
        </div>
        {renderLevelDetails()}

        {/* Analytics Statistics */}
        {analytics && (analytics.solves > 0 || analytics.fails > 0) && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '4px',
            fontSize: '0.85rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <TrendingUp size={14} color="#6b7280" />
              <strong style={{ color: '#374151' }}>Statistics:</strong>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <CheckCircle size={14} color="#10b981" />
                <span style={{ color: '#059669' }}>{analytics.solves} solved</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <XCircle size={14} color="#ef4444" />
                <span style={{ color: '#dc2626' }}>{analytics.fails} failed</span>
              </div>
            </div>
            <div style={{ marginTop: '0.25rem', color: '#6b7280' }}>
              Success rate: <strong style={{ color: analytics.successRate >= 50 ? '#059669' : '#dc2626' }}>
                {analytics.successRate.toFixed(1)}%
              </strong>
            </div>
          </div>
        )}
      </div>

      <div className="level-actions">
        <button
          onClick={() => onEdit(level)}
          className="btn btn-secondary btn-small"
        >
          <Edit size={16} />
          Edit
        </button>
        <button
          onClick={() => onDelete(level.id)}
          className="btn btn-danger btn-small"
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>
    </div>
  );
};

export default LevelCard;
